import {inject, Inject, Injectable, InjectionToken, Signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {
    BehaviorSubject,
    combineLatest,
    delay,
    distinctUntilChanged,
    EMPTY,
    forkJoin,
    map,
    mergeMap,
    Observable,
    of,
    shareReplay,
    tap,
} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import {HiddenValueWhileLoading, switchMapWithLoading} from '../rxjs';
import {filterNullish} from '../utils';

export type Translation = {
    key: string;
    application?: string;
    module?: string;
};

export type WebLangModule = {
    application: string;
    module: string;
};

export type TranslateConfig = {
    webLangModules: WebLangModule[];
    supportedLanguages: string[];
    defaultLanguage: string;
    defaultApplication: string;
    defaultModule: string;
    useFallbackLanguage?: boolean;
};

export const TRANSLATE_CONFIG = new InjectionToken<TranslateConfig>('translate.config');
export const TRANSLATE = new InjectionToken<TranslateService['translate']>('translate.function', {
    providedIn: 'root',
    factory: (): TranslateService['translate'] => {
        const service = inject(TranslateService);
        return service.translate.bind(service);
    },
});

@Injectable({providedIn: 'root'})
export class TranslateService {
    private languageSubject = new BehaviorSubject<string>(this.getValidatedLanguage(null));

    private readonly translationsPrimary$ = this.languageSubject.pipe(
        distinctUntilChanged(),
        switchMapWithLoading(language => this.initTranslations(language)),
        shareReplay(1),
    );

    private readonly translationsFallback$ = of(this.config).pipe(
        switchMapWithLoading(config =>
            config.useFallbackLanguage != null
                ? this.initTranslations(config.defaultLanguage)
                : of(new Map<string, Map<string, Map<string, string>>>()),
        ),
        shareReplay(1),
    );

    // For notifying components
    public readonly translation$ = combineLatest({
        primary: this.translationsPrimary$,
        fallback: this.translationsFallback$,
    }).pipe(
        map(({primary, fallback}) => !primary.loading && !fallback.loading),
        // The delay ensures that the texts in the app are reloaded when the language is changed.
        delay(0),
    );

    private readonly webLangUrl = 'https://webbill.brunata.dk/weblang/rest';

    private translationsPrimary = this.loadingValueObservableToSignal(this.translationsPrimary$);
    private translationsFallback = this.loadingValueObservableToSignal(this.translationsFallback$);

    constructor(
        private http: HttpClient,
        @Inject(TRANSLATE_CONFIG) private config: TranslateConfig,
    ) {}

    get language(): string {
        return this.languageSubject.value;
    }

    setLanguage(language: string | null): void {
        this.languageSubject.next(this.getValidatedLanguage(language));
    }

    private getValidatedLanguage(language: string | null): string {
        // Get language from browser
        // IE11 doesn't support getting languages from "navigator.languages". You can only get one language by calling
        // "navigator.language". Only option at the moment is to look at Accept-Language headers on a server-side service. The method used
        // below works in Chrome, Firefox and Edge.
        return this.getPreferredLanguage(
            [language, ...navigator.languages, navigator.language].filter(filterNullish),
        );
    }

    private getPreferredLanguage(languageList: string[]): string {
        const isLanguageSupported = (language: string): boolean =>
            this.config.supportedLanguages.includes(language);

        const chosenLanguage =
            languageList.find(preferredLanguage => isLanguageSupported(preferredLanguage)) ??
            languageList.find(preferredLanguage =>
                isLanguageSupported(preferredLanguage.split('-')[0]),
            ) ??
            this.config.defaultLanguage;

        return isLanguageSupported(chosenLanguage) ? chosenLanguage : chosenLanguage.split('-')[0];
    }

    getSupportedLanguages(): string[] {
        return this.config.supportedLanguages;
    }

    /**
     * translate a key
     * @param key - e.g. ORDERS
     * @param application - if null then 'webbill' is set.
     * @param module - if null then 'com.brunata.webbill.plsql' is set.
     * @param params - optional array of strings to replace {0}..{n-1} tokens in translations
     */
    public translate(
        key: string,
        application = this.config.defaultApplication,
        module = this.config.defaultModule,
        params: string[] = [],
    ): string {
        let translatedString =
            this.translationsPrimary()?.get(application)?.get(module)?.get(key) ??
            this.translationsFallback()?.get(application)?.get(module)?.get(key);
        if (translatedString !== undefined) {
            // Replace placeholders
            for (let i = 0; i < params.length; i++) {
                translatedString = translatedString.replace(`{${i}}`, params[i]);
            }
            return translatedString;
        }
        return `[${key}]`;
    }

    private initTranslations(
        language: string,
    ): Observable<Map<string, Map<string, Map<string, string>>>> {
        const translationObservables: Observable<Map<string, string>>[] = [];
        const translations: Map<string, Map<string, Map<string, string>>> = new Map();
        this.config.webLangModules.forEach(m => {
            // Create application if it doesn't exist.
            if (!translations.has(m.application)) {
                translations.set(m.application, new Map<string, Map<string, string>>());
            }
            const application = translations.get(m.application);

            // If Weblang is older than 48 hours, fetch new data from webservice
            const lastUpdateString = localStorage.getItem(
                `weblang.lastupdate.${m.application}.${m.module}.${language}`,
            );
            const translationsFromLocalStorage = localStorage.getItem(
                `weblang.translations.${m.application}.${m.module}.${language}`,
            );
            if (
                lastUpdateString != null &&
                translationsFromLocalStorage != null &&
                new Date().getTime() - new Date(lastUpdateString).getTime() < 48 * 60 * 60 * 1000
            ) {
                // Get translations from local storage
                application?.set(
                    m.module,
                    new Map<string, string>(
                        Object.entries(JSON.parse(translationsFromLocalStorage)),
                    ),
                );
            } else {
                // Refresh from server
                translationObservables.push(
                    this.fetchTranslationsFromWebLang(m.application, m.module, language).pipe(
                        tap(moduleTranslations => {
                            localStorage.setItem(
                                `weblang.translations.${m.application}.${m.module}.${language}`,
                                JSON.stringify(Object.fromEntries(moduleTranslations)),
                            );
                            localStorage.setItem(
                                `weblang.lastupdate.${m.application}.${m.module}.${language}`,
                                new Date().toUTCString(),
                            );
                            application?.set(m.module, moduleTranslations);
                        }),
                    ),
                );
            }
        });
        if (translationObservables.length > 0) {
            return forkJoin(translationObservables).pipe(map(() => translations));
        } else {
            return of(translations);
        }
    }

    private fetchTranslationsFromWebLang(
        application: string,
        module: string,
        language: string,
    ): Observable<Map<string, string>> {
        return this.http
            .get(`${this.webLangUrl}/messages/${application}/${module}/${language}`)
            .pipe(map(data => new Map<string, string>(Object.entries(data))));
    }

    private loadingValueObservableToSignal<T>(
        observable: Observable<HiddenValueWhileLoading<T>>,
    ): Signal<T | undefined> {
        return toSignal(
            observable.pipe(
                mergeMap(translations =>
                    !translations.loading && 'value' in translations
                        ? of(translations.value)
                        : EMPTY,
                ),
            ),
        );
    }
}
